'use client';
import React, { useState, useEffect, useRef } from 'react';

interface Product {
  id: number;
  name: string;
  shortDesc: string;
  fullDesc: string;
  image: string;
  alt: string;
  capacity: string;
  levels: string;
  type: string;
}

const products: Product[] = [
  {
    id: 1,
    name: 'Stack Parking System',
    shortDesc: 'Double your parking capacity',
    fullDesc: 'Our Stack Parking System is the most efficient entry-level automated solution, designed to double parking capacity by stacking two vehicles vertically in a single space. Ideal for residential complexes and small commercial spaces.',
    image: "https://images.unsplash.com/photo-1722768851768-9fa80d157d46",
    alt: 'Bhargav Parking stack parking system — two cars parked vertically one above the other in a compact bay',
    capacity: '2 Cars/Bay',
    levels: '2 Levels',
    type: 'Semi-Automated'
  },
  {
    id: 2,
    name: 'Puzzle Parking System',
    shortDesc: 'Intelligent multi-directional movement',
    fullDesc: 'The Puzzle Parking System accommodates 5 to 23 vehicles per module through intelligent three-level vertical and horizontal movement. Perfect for mid-size commercial and residential projects with space constraints.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1233dc04d-1786003596154.png",
    alt: 'Bhargav Parking puzzle parking system with cars arranged in a grid pattern moving horizontally and vertically',
    capacity: '5-23 Cars',
    levels: '3 Levels',
    type: 'Semi-Automated'
  },
  {
    id: 3,
    name: 'Tower Parking System',
    shortDesc: 'Vertical tower for high-density parking',
    fullDesc: 'Tower Parking is our flagship vertical solution accommodating 20 to 100 vehicles in a minimal footprint. Ideal for urban commercial complexes, malls, and high-rise residential buildings where land is at a premium.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1d12e2f0c-1772916679853.png",
    alt: 'Bhargav Parking tower parking structure with multiple levels of cars stored vertically in an urban environment',
    capacity: '20-100 Cars',
    levels: 'Up to 15 Levels',
    type: 'Fully Automated'
  },
  {
    id: 4,
    name: 'Shuttle / Robotic Parking',
    shortDesc: 'AI-driven robotic automation',
    fullDesc: 'Our Shuttle and Robotic Parking Systems redefine urban parking with AI-driven automation accommodating 2 to 300 vehicles per installation. Zero human intervention, maximum efficiency, and complete safety for every vehicle.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_187891cc7-1785956178082.png",
    alt: 'Bhargav Parking shuttle system — automated vehicle retrieval mechanism in a modern robotic parking facility',
    capacity: '2-300 Cars',
    levels: 'Multi-Level',
    type: 'Fully Robotic'
  },
  {
    id: 5,
    name: 'Rotary Parking System',
    shortDesc: 'Carousel-style compact parking',
    fullDesc: 'The Rotary Parking System is our compact carousel-style solution designed for tight urban spaces. Vehicles rotate on a circular platform, allowing efficient retrieval without complex navigation.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1feb9ecf5-1786003597837.png",
    alt: 'Bhargav Parking rotary carousel parking system with vehicles arranged in a circular rotating platform',
    capacity: '6-20 Cars',
    levels: '2-3 Levels',
    type: 'Semi-Automated'
  },
  {
    id: 6,
    name: 'Horizontal Circulation',
    shortDesc: 'Smooth horizontal movement system',
    fullDesc: 'Our Horizontal Circulation Parking System provides smooth, efficient vehicle movement across large parking facilities. Ideal for airports, hospitals, and large commercial complexes requiring high throughput.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1a9d53334-1785956178073.png",
    alt: 'Bhargav Parking horizontal circulation system showing cars moving on a flat automated platform in a large facility',
    capacity: '10-50 Cars',
    levels: '1-2 Levels',
    type: 'Automated'
  },
  {
    id: 7,
    name: 'Two Wheeler Parking',
    shortDesc: 'Smart solutions for two-wheelers',
    fullDesc: "With India being the world's largest two-wheeler market, our dedicated Two Wheeler Parking Systems provide organized, space-efficient solutions for motorcycles and scooters in commercial and residential spaces.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_187de93a5-1785956178592.png",
    alt: 'Bhargav Parking two-wheeler parking system with motorcycles and scooters organized in a vertical automated rack',
    capacity: '20-100 Bikes',
    levels: '2-4 Levels',
    type: 'Semi-Automated'
  },
];

export default function ProductsSection() {
  const [activeTab, setActiveTab] = useState(0);
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const handleScrollToContact = () => {
    const el = document.getElementById('contact');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="products"
      className="py-20"
      style={{ backgroundColor: '#0a2d4a', fontFamily: 'Ubuntu, sans-serif' }}
      ref={ref}
    >
      <div className="max-w-[1200px] mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12">
          <div>
            <p className="text-[#00AAE2] text-[15px] font-medium tracking-[2px] uppercase mb-3">What We Offer</p>
            <h2
              className="text-white font-medium"
              style={{ fontSize: '42px', lineHeight: '52px', fontFamily: 'Ubuntu, sans-serif' }}>
              
              Our Parking Systems
            </h2>
          </div>
          <p
            className="text-white/70 max-w-[420px]"
            style={{ fontSize: '17px', lineHeight: '28px', fontFamily: 'Ubuntu, sans-serif' }}>
            
            We design and deliver smart parking solutions that solve the challenges of limited urban space. Each system is built for safety, reliability, and seamless automation.
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="mb-8" style={{ overflowX: 'auto', scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
          <style>{`.products-tabs::-webkit-scrollbar { display: none; }`}</style>
          <div
            className="products-tabs flex gap-2 min-w-max"
            style={{ borderBottom: '1px solid rgba(255,255,255,0.15)', paddingBottom: '0' }}>
            
            {products.map((p, i) =>
            <button
              key={p.id}
              onClick={() => setActiveTab(i)}
              className={`relative px-4 py-3 text-[15px] transition-all duration-300 whitespace-nowrap ${
              activeTab === i ?
              'text-white' : 'text-white/50 hover:text-white/80'}`
              }
              style={{ fontFamily: 'Ubuntu, sans-serif' }}>
              
                {p.name}
                {activeTab === i &&
              <span
                className="absolute bottom-0 left-0 w-3/4 h-[2px] bg-white"
                style={{ transition: 'all 0.3s ease' }} />

              }
              </button>
            )}
            <a
              href="#contact"
              onClick={(e) => {e.preventDefault();handleScrollToContact();}}
              className="ml-auto px-4 py-3 text-[#00AAE2] text-[15px] hover:text-white transition-colors duration-300 whitespace-nowrap"
              style={{ fontFamily: 'Ubuntu, sans-serif' }}>
              
              View All →
            </a>
          </div>
        </div>

        {/* Product Display */}
        <div
          className={`transition-all duration-500 ${
          visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`
          }>
          
          {products.map((product, i) =>
          <div
            key={product.id}
            className={`transition-all duration-500 ${
            activeTab === i ? 'block' : 'hidden'}`
            }>
            
              {/* Wide horizontal card — image fills right half, gradient overlay on left */}
              <div
              className="relative rounded-[24px] overflow-hidden"
              style={{ height: '340px', boxShadow: '0 0 0 1px rgba(255,255,255,0.12), 0 8px 40px rgba(0,0,0,0.5)' }}>
              
                {/* Full-bleed background image */}
                <img
                src={product.image}
                alt={product.alt}
                className="absolute inset-0 w-full h-full object-cover" />
                
                {/* Dark gradient overlay — covers left ~55% fading to transparent */}
                <div
                className="absolute inset-0"
                style={{ background: 'linear-gradient(to right, rgba(2,18,36,0.98) 0%, rgba(2,18,36,0.94) 35%, rgba(2,18,36,0.70) 55%, transparent 75%)' }} />

                {/* Content on top of gradient */}
                <div className="relative z-10 h-full flex flex-col justify-center px-10 lg:px-14" style={{ maxWidth: '520px' }}>
                  {/* Badges */}
                  <div className="flex flex-wrap gap-2 mb-5">
                    <span
                    className="px-3 py-1 rounded-full text-[12px] font-medium"
                    style={{ backgroundColor: 'rgba(0,170,226,0.25)', color: '#00AAE2', fontFamily: 'Ubuntu, sans-serif', border: '1px solid rgba(0,170,226,0.4)' }}>
                      {product.type}
                    </span>
                    <span
                    className="px-3 py-1 rounded-full text-[12px] font-medium"
                    style={{ backgroundColor: 'rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.8)', fontFamily: 'Ubuntu, sans-serif' }}>
                      {product.capacity}
                    </span>
                    <span
                    className="px-3 py-1 rounded-full text-[12px] font-medium"
                    style={{ backgroundColor: 'rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.8)', fontFamily: 'Ubuntu, sans-serif' }}>
                      {product.levels}
                    </span>
                  </div>

                  {/* Title */}
                  <h3
                  className="text-white font-semibold mb-3"
                  style={{ fontSize: '28px', lineHeight: '36px', fontFamily: 'Ubuntu, sans-serif' }}>
                    {product.name}
                  </h3>

                  {/* Short description */}
                  <p
                  className="mb-6"
                  style={{ fontSize: '15px', lineHeight: '24px', color: 'rgba(255,255,255,0.72)', fontFamily: 'Ubuntu, sans-serif', maxWidth: '380px' }}>
                    {product.shortDesc}
                  </p>

                  {/* CTA Button */}
                  <div>
                    <a
                    href="#contact"
                    onClick={(e) => { e.preventDefault(); handleScrollToContact(); }}
                    className="inline-flex items-center gap-2 bg-white text-[#062543] font-medium rounded-full px-6 py-3 text-[14px] hover:bg-[#00AAE2] hover:text-white transition-all duration-300"
                    style={{ fontFamily: 'Ubuntu, sans-serif' }}>
                      Get Quote
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M3 8H13M13 8L9 4M13 8L9 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>);
}
